import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	List,
	ListItem,
	ListItemText,
	Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { groupWhatsNewByVersion, type WhatsNewEntry } from '../../whats-new';
import { whatsNewPage } from '../../store/fixed-routing';

type Props = {
	entries: WhatsNewEntry[];
	open: boolean;
	onDismiss: () => Promise<void>;
};

const WhatsNewDialog = ({ entries, open, onDismiss }: Props) => {
	const [saving, setSaving] = useState(false);
	const groups = useMemo(() => groupWhatsNewByVersion(entries), [entries]);

	if (entries.length === 0) {
		return null;
	}

	const dismiss = async () => {
		setSaving(true);
		try {
			await onDismiss();
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={() => {
				void dismiss();
			}}
			maxWidth='sm'
			fullWidth
		>
			<DialogTitle>Co nowego w aplikacji?</DialogTitle>
			<DialogContent dividers>
				<DialogContentText sx={{ mb: 2 }}>
					Skrót zmian od ostatniego logowania. Pełna lista — na stronie „Co nowego”.
				</DialogContentText>
				{groups.map(({ version, items }) => (
					<React.Fragment key={version}>
						<Typography
							variant='subtitle2'
							color='text.secondary'
							sx={{ mt: 1, mb: 0.5 }}
						>
							Wersja {version}
						</Typography>
						{items.map((item, idx) => (
							<React.Fragment key={`${version}-${item.title}-${idx}`}>
								<Typography
									variant='subtitle1'
									fontWeight={600}
									sx={{ mt: idx === 0 ? 0 : 1.5 }}
								>
									{item.title}
								</Typography>
								<List
									dense
									sx={{ py: 0 }}
								>
									{item.bullets.map((bullet, bIdx) => (
										<ListItem
											key={bIdx}
											sx={{ py: 0.25, alignItems: 'flex-start' }}
										>
											<ListItemText
												primary={bullet}
												primaryTypographyProps={{ variant: 'body2' }}
											/>
										</ListItem>
									))}
								</List>
							</React.Fragment>
						))}
					</React.Fragment>
				))}
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
				<Button
					component={RouterLink}
					to={whatsNewPage}
					onClick={() => {
						void dismiss();
					}}
				>
					Pełna lista
				</Button>
				<Button
					variant='contained'
					disabled={saving}
					onClick={() => {
						void dismiss();
					}}
				>
					Rozumiem
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default WhatsNewDialog;
